import { Box, CircularProgress, Collapse } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useStore } from 'logic';
import { useForm, Controller, Control } from 'react-hook-form';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { designTokens } from 'theme';

interface DuplicateSessionProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  /** When opening from a list (e.g. Projects), pass ownership from the list item to avoid lookup/timing issues */
  isOwnerOverride?: boolean;
}

interface DuplicateSessionFormState {
  name: string;
  sources: boolean;
  sourceRows: boolean;
  modules: boolean;
  filenameFormula: boolean;
  queueCards: boolean;
  resetCardStatus: 'true' | 'false';
}

// Chevron icon for expand/collapse
const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    style={{
      width: 14,
      height: 14,
      transition: `transform ${designTokens.transitions.normal}`,
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Info icon for tooltips
const InfoIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const DuplicateSwitch = ({
  label,
  control,
  name,
  infoText,
  disabled,
}: {
  control: Control<DuplicateSessionFormState>;
  name: keyof DuplicateSessionFormState;
  label: string;
  infoText: string;
  disabled?: boolean;
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 0',
            borderBottom: `1px solid ${designTokens.colors.border.subtle}`,
            opacity: disabled ? 0.5 : 1,
            '&:last-child': { borderBottom: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                fontSize: designTokens.typography.fontSize.sm,
                color: designTokens.colors.text.primary,
                fontFamily: designTokens.typography.fontMono,
              }}
            >
              {label}
            </Box>
            <Box
              sx={{ position: 'relative' }}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <Box
                sx={{
                  color: designTokens.colors.text.dim,
                  cursor: 'help',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <InfoIcon />
              </Box>
              {showTooltip && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: designTokens.colors.bg.elevated,
                    border: `1px solid ${designTokens.colors.border.hover}`,
                    borderRadius: designTokens.spacing.radiusMd,
                    padding: '8px 12px',
                    fontSize: designTokens.typography.fontSize.xs,
                    color: designTokens.colors.text.secondary,
                    whiteSpace: 'nowrap',
                    maxWidth: 280,
                    zIndex: 100,
                  }}
                >
                  {infoText}
                </Box>
              )}
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => !disabled && onChange(!value)}
            sx={{
              width: 44,
              height: 24,
              bgcolor: value ? designTokens.colors.accent.primary : designTokens.colors.bg.card,
              border: `1px solid ${
                value ? designTokens.colors.accent.primary : designTokens.colors.border.default
              }`,
              borderRadius: '12px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              position: 'relative',
              transition: `all ${designTokens.transitions.fast}`,
              '&:hover': {
                borderColor: disabled ? undefined : designTokens.colors.border.hover,
              },
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                bgcolor: '#fff',
                borderRadius: '50%',
                position: 'absolute',
                top: 2,
                left: value ? 22 : 2,
                transition: `all ${designTokens.transitions.fast}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }}
            />
          </Box>
        </Box>
      )}
    />
  );
};

/**
 * Extract a user-friendly error message from various error types
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const { message } = error;
    if (message.includes('Invalid options:')) return message;
    if (message.includes('permission-denied'))
      return 'You do not have permission to duplicate this project.';
    if (message.includes('not-found'))
      return 'The project was not found. It may have been deleted.';
    if (message.includes('Failed to duplicate')) return message;
    return message || 'An unexpected error occurred while duplicating the project.';
  }
  return 'An unexpected error occurred while duplicating the project.';
}

export const DuplicateSession = observer(
  ({ open, onClose, sessionId, isOwnerOverride }: DuplicateSessionProps) => {
    const {
      sessionsStore: { sessions, sharedSessions, duplicateSession },
      authStore: { user },
      uiDuplicationRequestStore,
    } = useStore();
    const [duplicateLoading, setDuplicateLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [requestMessage, setRequestMessage] = useState('');
    const isMountedRef = useRef(true);

    // Track mounted state to prevent setState on unmounted component
    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    // Resolve session from both owned and shared (Projects list uses allSessions = ordered + shared)
    const session = sessions[sessionId] ?? sharedSessions[sessionId];
    const originalSessionName = session?.data?.name ?? 'Session';

    // Check if user is owner or editor. Prefer isOwnerOverride when provided (e.g. from Projects list)
    // so we use the same session/ownership that was in the list, avoiding lookup or timing issues.
    const isOwner =
      isOwnerOverride !== undefined
        ? isOwnerOverride
        : Boolean(user?.uid && session?.isUserOwner(user.uid));
    // When we know user is not owner (isOwnerOverride === false), show request form even if session not yet loaded
    const isEditor = isOwnerOverride === false || Boolean(session?.canEditSession && !isOwner);
    const defaultValues: DuplicateSessionFormState = useMemo(
      () => ({
        name: `${originalSessionName} (Copy)`,
        sources: true,
        sourceRows: true,
        modules: true,
        filenameFormula: true,
        queueCards: true,
        resetCardStatus: 'false',
      }),
      [originalSessionName],
    );

    const { control, handleSubmit, watch, setValue, reset } = useForm<DuplicateSessionFormState>({
      defaultValues,
    });

    // Reset form when dialog opens with new session
    useEffect(() => {
      if (open) {
        reset({
          name: `${originalSessionName} (Copy)`,
          sources: true,
          sourceRows: true,
          modules: true,
          filenameFormula: true,
          queueCards: true,
          resetCardStatus: 'false',
        });
        setError(null);
        setExpanded(false);
      }
    }, [open, originalSessionName, reset]);

    const isSourceSelected = watch('sources');
    const isSourceRowsSelected = watch('sourceRows');
    const isModuleSelected = watch('modules');
    const isCardSelected = watch('queueCards');

    // set some options off if duplicate source structure is off
    useEffect(() => {
      if (!isSourceSelected) {
        setValue('sourceRows', false);
        setValue('modules', false);
      }
    }, [isSourceSelected, setValue]);

    // set some options off if modules is off
    useEffect(() => {
      if (!isModuleSelected) {
        setValue('filenameFormula', false);
        setValue('queueCards', false);
      }
    }, [isModuleSelected, setValue]);

    // If queueCards is enabled, sourceRows must also be enabled
    useEffect(() => {
      if (isCardSelected && !isSourceRowsSelected) {
        setValue('sourceRows', true);
      }
    }, [isCardSelected, isSourceRowsSelected, setValue]);

    const handleDuplicateSession = useCallback(
      async (options: DuplicateSessionFormState) => {
        setDuplicateLoading(true);
        setError(null);

        try {
          // If editor, request duplication instead of duplicating directly
          if (isEditor) {
            await uiDuplicationRequestStore.requestDuplication(
              session.id,
              options.name,
              {
                sources: options.sources,
                sourceRows: options.sourceRows,
                modules: options.modules,
                filenameFormula: options.filenameFormula,
                queueCards: options.queueCards,
                resetCardStatus: options.resetCardStatus === 'true',
              },
              requestMessage || undefined,
            );
            if (isMountedRef.current) {
              onClose();
            }
          } else {
            // Owner can duplicate directly
            await duplicateSession(session.id, {
              ...options,
              resetCardStatus: options.resetCardStatus === 'true',
            });
            if (isMountedRef.current) {
              onClose();
            }
          }
        } catch (e) {
          if (!isMountedRef.current) return;
          const errorMessage = getErrorMessage(e);
          setError(errorMessage);
          console.error('Failed to duplicate session:', e);
        } finally {
          if (isMountedRef.current) {
            setDuplicateLoading(false);
          }
        }
      },
      [duplicateSession, uiDuplicationRequestStore, session?.id, onClose, isEditor, requestMessage],
    );

    const onSubmit = handleSubmit(handleDuplicateSession);

    const toggleExpand = useCallback(() => {
      setExpanded((prev) => !prev);
    }, []);

    const handleClose = useCallback(() => {
      if (!duplicateLoading) {
        setError(null);
        onClose();
      }
    }, [duplicateLoading, onClose]);

    if (!open) return null;

    // Handle case where session is not found
    if (!session) {
      return (
        <Box
          onClick={handleClose}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: designTokens.colors.bg.elevated,
              border: `1px solid ${designTokens.colors.border.hover}`,
              borderRadius: designTokens.spacing.radiusLg,
              padding: '24px',
              maxWidth: 400,
              width: '90%',
            }}
          >
            <Box
              sx={{
                fontSize: designTokens.typography.fontSize.base,
                fontWeight: designTokens.typography.fontWeight.bold,
                color: designTokens.colors.text.primary,
                mb: 2,
              }}
            >
              Error
            </Box>
            <Box
              sx={{
                fontSize: designTokens.typography.fontSize.sm,
                color: designTokens.colors.text.secondary,
                mb: 3,
              }}
            >
              Session not found. It may have been deleted.
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box
                component="button"
                onClick={handleClose}
                sx={{
                  padding: '10px 16px',
                  bgcolor: 'transparent',
                  border: `1px solid ${designTokens.colors.border.default}`,
                  borderRadius: designTokens.spacing.radiusSm,
                  fontFamily: designTokens.typography.fontMono,
                  fontSize: designTokens.typography.fontSize.sm,
                  color: designTokens.colors.text.secondary,
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: designTokens.colors.border.hover,
                    color: designTokens.colors.text.primary,
                  },
                }}
              >
                Close
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        onClick={handleClose}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300,
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            bgcolor: designTokens.colors.bg.elevated,
            border: `1px solid ${designTokens.colors.border.hover}`,
            borderRadius: designTokens.spacing.radiusLg,
            padding: '24px',
            maxWidth: 480,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              fontSize: designTokens.typography.fontSize.lg,
              fontWeight: designTokens.typography.fontWeight.bold,
              color: designTokens.colors.text.primary,
              mb: 3,
            }}
          >
            {isEditor ? 'Request to Duplicate Project' : 'Duplicate Project'}
          </Box>

          {/* Editor notice */}
          {isEditor && (
            <Box
              sx={{
                bgcolor: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: designTokens.spacing.radiusSm,
                padding: '12px 16px',
                mb: 3,
                fontSize: designTokens.typography.fontSize.sm,
                color: designTokens.colors.text.secondary,
              }}
            >
              As an editor, you need approval from the project owner to duplicate this project.
            </Box>
          )}

          {/* Error alert */}
          {error && (
            <Box
              sx={{
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: designTokens.spacing.radiusSm,
                padding: '12px 16px',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  fontSize: designTokens.typography.fontSize.sm,
                  color: '#ef4444',
                }}
              >
                {error}
              </Box>
              <Box
                component="button"
                onClick={() => setError(null)}
                sx={{
                  bgcolor: 'transparent',
                  border: 'none',
                  color: '#ef4444',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                  padding: 0,
                  '&:hover': { opacity: 0.7 },
                }}
              >
                ×
              </Box>
            </Box>
          )}

          {/* Form */}
          <form onSubmit={onSubmit}>
            {/* Name input */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  fontSize: designTokens.typography.fontSize.xs,
                  color: designTokens.colors.text.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  mb: 1,
                }}
              >
                New Project Name
              </Box>
              <Controller
                control={control}
                name="name"
                rules={{ required: 'Project name is required' }}
                render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                  <>
                    <Box
                      component="input"
                      type="text"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ref={ref}
                      autoFocus
                      disabled={duplicateLoading}
                      sx={{
                        width: '100%',
                        padding: '12px 14px',
                        bgcolor: designTokens.colors.bg.card,
                        border: `1px solid ${
                          error ? '#ef4444' : designTokens.colors.border.default
                        }`,
                        borderRadius: designTokens.spacing.radiusSm,
                        fontFamily: designTokens.typography.fontMono,
                        fontSize: designTokens.typography.fontSize.sm,
                        color: designTokens.colors.text.primary,
                        outline: 'none',
                        transition: `all ${designTokens.transitions.fast}`,
                        '&:focus': {
                          borderColor: error ? '#ef4444' : designTokens.colors.border.hover,
                        },
                        '&::placeholder': {
                          color: designTokens.colors.text.dim,
                        },
                        '&:disabled': {
                          opacity: 0.5,
                          cursor: 'not-allowed',
                        },
                      }}
                    />
                    {error && (
                      <Box
                        sx={{
                          fontSize: designTokens.typography.fontSize.xs,
                          color: '#ef4444',
                          mt: 0.5,
                        }}
                      >
                        {error.message}
                      </Box>
                    )}
                  </>
                )}
              />
            </Box>

            {/* Message field for editors */}
            {isEditor && (
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    fontSize: designTokens.typography.fontSize.xs,
                    color: designTokens.colors.text.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    mb: 1,
                  }}
                >
                  Message to Owner (Optional)
                </Box>
                <Box
                  component="textarea"
                  value={requestMessage}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setRequestMessage(e.target.value)
                  }
                  disabled={duplicateLoading}
                  placeholder="Add a message explaining why you want to duplicate this project..."
                  rows={3}
                  sx={{
                    width: '100%',
                    padding: '12px 14px',
                    bgcolor: designTokens.colors.bg.card,
                    border: `1px solid ${designTokens.colors.border.default}`,
                    borderRadius: designTokens.spacing.radiusSm,
                    fontFamily: designTokens.typography.fontMono,
                    fontSize: designTokens.typography.fontSize.sm,
                    color: designTokens.colors.text.primary,
                    outline: 'none',
                    resize: 'vertical',
                    transition: `all ${designTokens.transitions.fast}`,
                    '&:focus': {
                      borderColor: designTokens.colors.border.hover,
                    },
                    '&::placeholder': {
                      color: designTokens.colors.text.dim,
                    },
                    '&:disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                    },
                  }}
                />
              </Box>
            )}

            {/* Advanced Options */}
            <Box
              sx={{
                border: `1px solid ${designTokens.colors.border.default}`,
                borderRadius: designTokens.spacing.radiusMd,
                overflow: 'hidden',
              }}
            >
              <Box
                onClick={toggleExpand}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 16px',
                  cursor: 'pointer',
                  bgcolor: designTokens.colors.bg.card,
                  '&:hover': {
                    bgcolor: designTokens.colors.bg.cardHover,
                  },
                }}
              >
                <Box
                  sx={{
                    fontSize: designTokens.typography.fontSize.sm,
                    color: designTokens.colors.text.secondary,
                    fontFamily: designTokens.typography.fontMono,
                  }}
                >
                  Advanced Options
                </Box>
                <Box sx={{ color: designTokens.colors.text.muted }}>
                  <ChevronIcon isOpen={expanded} />
                </Box>
              </Box>

              <Collapse in={expanded}>
                <Box sx={{ padding: '0 16px 16px' }}>
                  <DuplicateSwitch
                    name="sources"
                    label="Library Structure"
                    control={control}
                    disabled={duplicateLoading}
                    infoText="Copy sources and their column headers"
                  />
                  <DuplicateSwitch
                    name="sourceRows"
                    label="Library Data"
                    control={control}
                    disabled={duplicateLoading || !isSourceSelected || isCardSelected}
                    infoText={
                      isCardSelected
                        ? 'Required when duplicating cards'
                        : 'Copy all data rows from sources'
                    }
                  />
                  <DuplicateSwitch
                    name="modules"
                    label="Card Modules"
                    disabled={duplicateLoading || !isSourceSelected}
                    control={control}
                    infoText="Copy the module structure"
                  />
                  <DuplicateSwitch
                    name="filenameFormula"
                    label="Filename Formula"
                    disabled={duplicateLoading || !isModuleSelected}
                    control={control}
                    infoText="Copy the filename formula configuration"
                  />
                  <DuplicateSwitch
                    name="queueCards"
                    label="Queue Cards"
                    control={control}
                    disabled={duplicateLoading || !isModuleSelected}
                    infoText="Copy all cards with their connections"
                  />

                  {/* Card status radio options */}
                  {isCardSelected && (
                    <Controller
                      control={control}
                      name="resetCardStatus"
                      render={({ field: { onChange, value } }) => (
                        <Box sx={{ mt: 2, pl: 1 }}>
                          <Box
                            sx={{
                              fontSize: designTokens.typography.fontSize.xs,
                              color: designTokens.colors.text.muted,
                              textTransform: 'uppercase',
                              letterSpacing: '1px',
                              mb: 1.5,
                            }}
                          >
                            Card Status
                          </Box>
                          {[
                            { val: 'false', label: 'Preserve current status' },
                            { val: 'true', label: 'Reset all to Active' },
                          ].map((opt) => (
                            <Box
                              key={opt.val}
                              component="label"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                padding: '8px 0',
                                cursor: duplicateLoading ? 'not-allowed' : 'pointer',
                                opacity: duplicateLoading ? 0.5 : 1,
                              }}
                            >
                              <Box
                                component="input"
                                type="radio"
                                name="resetCardStatus"
                                value={opt.val}
                                checked={value === opt.val}
                                onChange={() => onChange(opt.val)}
                                disabled={duplicateLoading}
                                sx={{
                                  width: 16,
                                  height: 16,
                                  accentColor: designTokens.colors.accent.primary,
                                }}
                              />
                              <Box
                                sx={{
                                  fontSize: designTokens.typography.fontSize.sm,
                                  color: designTokens.colors.text.secondary,
                                }}
                              >
                                {opt.label}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    />
                  )}
                </Box>
              </Collapse>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 3 }}>
              <Box
                component="button"
                type="button"
                onClick={handleClose}
                disabled={duplicateLoading}
                sx={{
                  padding: '10px 16px',
                  bgcolor: 'transparent',
                  border: `1px solid ${designTokens.colors.border.default}`,
                  borderRadius: designTokens.spacing.radiusSm,
                  fontFamily: designTokens.typography.fontMono,
                  fontSize: designTokens.typography.fontSize.sm,
                  color: designTokens.colors.text.secondary,
                  cursor: duplicateLoading ? 'not-allowed' : 'pointer',
                  transition: `all ${designTokens.transitions.fast}`,
                  opacity: duplicateLoading ? 0.5 : 1,
                  '&:hover': {
                    borderColor: designTokens.colors.border.hover,
                    color: designTokens.colors.text.primary,
                  },
                }}
              >
                Cancel
              </Box>
              <Box
                component="button"
                type="submit"
                disabled={duplicateLoading}
                sx={{
                  padding: '10px 20px',
                  bgcolor: designTokens.colors.accent.primary,
                  border: 'none',
                  borderRadius: designTokens.spacing.radiusSm,
                  fontFamily: designTokens.typography.fontMono,
                  fontSize: designTokens.typography.fontSize.sm,
                  fontWeight: designTokens.typography.fontWeight.bold,
                  color: '#fff',
                  cursor: duplicateLoading ? 'not-allowed' : 'pointer',
                  transition: `all ${designTokens.transitions.fast}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 100,
                  '&:hover': {
                    bgcolor: duplicateLoading ? undefined : designTokens.colors.accent.primaryHover,
                  },
                }}
              >
                {duplicateLoading ? (
                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                ) : isEditor ? (
                  'Request Duplication'
                ) : (
                  'Duplicate'
                )}
              </Box>
            </Box>
          </form>
        </Box>
      </Box>
    );
  },
);
